import React, { useState } from 'react';
import API from '../api/api';

function EDIModal({ isOpen, onClose, shipment, ediType, canSend210 = true }) {
  const [formData, setFormData] = useState({
    // EDI 214 fields
    status_code: 'P1',
    status_date: '',
    status_time: '',
    location: '',
    notes: '',
    new_shipment_status: '',
    
    // EDI 210 fields
    send_edi_210: true,
    edi_vendor: 'opentext',  // Hardcoded to Opentext - handled behind the scenes
    invoice_number: '',
    invoice_date: '',
    payment_terms: 'NET30',
    freight_charges: '',
    accessorial_charges: '0.00',
    fuel_surcharge: '0.00',
    currency: 'USD',
    lading_quantity: '1',
    total_amount: '',
    mark_delivered: false
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Initialize form data when shipment changes
  React.useEffect(() => {
    if (shipment) {
      setFormData(prev => ({
        ...prev,
        invoice_number: `INV-${shipment.pro_number}`,
        freight_charges: shipment.rate || '0.00',
        total_amount: shipment.rate || '0.00'
      }));
    }
  }, [shipment]);

  // Force send_edi_210 off when there is no outbound EDI connection for this shipper
  React.useEffect(() => {
    if (!canSend210) {
      setFormData(prev => ({ ...prev, send_edi_210: false }));
    }
  }, [canSend210]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Auto-calculate total for EDI 210
    if (ediType === '210' && (name === 'freight_charges' || name === 'accessorial_charges' || name === 'fuel_surcharge')) {
      const freight = name === 'freight_charges' ? parseFloat(value) || 0 : parseFloat(formData.freight_charges) || 0;
      const accessorial = name === 'accessorial_charges' ? parseFloat(value) || 0 : parseFloat(formData.accessorial_charges) || 0;
      const fuel = name === 'fuel_surcharge' ? parseFloat(value) || 0 : parseFloat(formData.fuel_surcharge) || 0;
      setFormData(prev => ({
        ...prev,
        total_amount: (freight + accessorial + fuel).toFixed(2)
      }));
    }
  };

  const handleDownload = (filename) => {
    // Download the EDI file
    window.open(`${API.defaults.baseURL}/edi-files/${filename}/download/`, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (ediType === '214') {
        // EDI 214 - always send EDI
        const response = await API.post(`shipments/${shipment.id}/generate-edi-214/`, formData);
        
        setSuccess({
          message: response.data.message,
          filename: response.data.filename,
          has_edi_setup: response.data.has_edi_setup,
          edi_sent: response.data.edi_sent
        });
      } else {
        // EDI 210 - conditionally send EDI based on checkbox
        if (formData.send_edi_210) {
          // Send EDI 210 with invoice creation
          const response = await API.post(`shipments/${shipment.id}/generate-edi-210/`, formData);
          
          setSuccess({
            message: response.data.message,
            filename: response.data.filename,
            invoice_number: response.data.invoice_number,
            total_amount: response.data.total_amount,
            has_edi_setup: response.data.has_edi_setup,
            edi_sent: response.data.edi_sent
          });
        } else {
          // Just create invoice without EDI
          const response = await API.post(`shipments/${shipment.id}/create-invoice/`, formData);
          
          setSuccess({
            message: response.data.message,
            invoice_number: response.data.invoice_number,
            total_amount: response.data.total_amount
          });
        }
      }
      
      // Don't auto-close if we have a file to download
      // Close modal after 5 seconds if there's a filename
      if (success?.filename) {
        setTimeout(() => {
          onClose();
          setSuccess(null);
        }, 5000);
      }
      
    } catch (err) {
      console.error('EDI generation error:', err);
      setError(err.response?.data?.error || 'Failed to generate EDI file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusCodeOptions = () => {
    const options = [
      { value: 'P1', label: 'P1 - Picked Up' },
      { value: 'I1', label: 'I1 - In Transit' },
      { value: 'D1', label: 'D1 - Delivered' },
      { value: 'X1', label: 'X1 - Exception' },
      { value: 'H1', label: 'H1 - Hold' }
    ];
    return options;
  };

  const getShipmentStatusOptions = () => {
    const options = [
      { value: 'pending', label: 'Pending' },
      { value: 'in_transit', label: 'In Transit' },
      { value: 'delivered', label: 'Delivered' },
      { value: 'cancelled', label: 'Cancelled' }
    ];
    return options;
  };

  if (!isOpen || !shipment) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {ediType === '210' ? 'Create Invoice' : `Generate EDI ${ediType}`} - Shipment {shipment.pro_number}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <div className="flex flex-col">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">{success.message}</h3>
                  <p className="text-sm text-green-700 mt-1">
                    File: {success.filename}
                    {success.invoice_number && (
                      <span> | Invoice: {success.invoice_number}</span>
                    )}
                    {success.total_amount && (
                      <span> | Total: ${success.total_amount}</span>
                    )}
                  </p>
                  {success.filename && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => handleDownload(success.filename)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <svg className="mr-2 -ml-0.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download EDI File
                      </button>
                      {!success.edi_sent && (
                        <p className="text-xs text-green-600 mt-2">
                          Note: EDI file was not sent automatically as EDI is not configured for your organization. Download the file to send manually.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {ediType === '214' ? (
              // EDI 214 Form Fields
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status Code *
                    </label>
                    <select
                      name="status_code"
                      value={formData.status_code}
                      onChange={handleInputChange}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                      required
                    >
                      {getStatusCodeOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status Date *
                    </label>
                    <input
                      type="date"
                      name="status_date"
                      value={formData.status_date}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status Time
                    </label>
                    <input
                      type="time"
                      name="status_time"
                      value={formData.status_time}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Location
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="City, State"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Additional notes about the status update..."
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Update Shipment Status
                  </label>
                  <select
                    name="new_shipment_status"
                    value={formData.new_shipment_status}
                    onChange={handleInputChange}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  >
                    <option value="">No change</option>
                    {getShipmentStatusOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              // EDI 210 Form Fields
              <>
                {/* EDI 210 Option — only shown when the carrier has an outbound 210 connection */}
                {canSend210 ? (
                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      name="send_edi_210"
                      checked={formData.send_edi_210}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Send EDI 210 (Invoice) along with invoice creation
                    </label>
                  </div>
                ) : (
                  <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 p-3">
                    <p className="text-sm text-amber-800">
                      <span className="font-medium">EDI 210 unavailable.</span>{' '}
                      No outbound EDI connection is configured for this shipper.
                      The invoice will be created in the system only.
                    </p>
                  </div>
                )}

                {/* EDI vendor is handled automatically behind the scenes (Opentext) */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Invoice Number *
                    </label>
                    <input
                      type="text"
                      name="invoice_number"
                      value={formData.invoice_number}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Invoice Date *
                    </label>
                    <input
                      type="date"
                      name="invoice_date"
                      value={formData.invoice_date}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Payment Terms
                    </label>
                    <select
                      name="payment_terms"
                      value={formData.payment_terms}
                      onChange={handleInputChange}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                    >
                      <option value="NET30">Net 30</option>
                      <option value="NET15">Net 15</option>
                      <option value="NET7">Net 7</option>
                      <option value="DUE">Due on Receipt</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Freight Charges *
                    </label>
                    <input
                      type="number"
                      name="freight_charges"
                      value={formData.freight_charges}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Accessorial Charges
                    </label>
                    <input
                      type="number"
                      name="accessorial_charges"
                      value={formData.accessorial_charges}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Fuel Surcharge
                    </label>
                    <input
                      type="number"
                      name="fuel_surcharge"
                      value={formData.fuel_surcharge}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Currency
                    </label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                    >
                      <option value="USD">USD - US Dollar</option>
                      <option value="CAD">CAD - Canadian Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Lading Quantity
                    </label>
                    <input
                      type="number"
                      name="lading_quantity"
                      value={formData.lading_quantity}
                      onChange={handleInputChange}
                      min="1"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Total Amount
                    </label>
                    <input
                      type="number"
                      name="total_amount"
                      value={formData.total_amount}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-gray-50"
                      readOnly
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="mark_delivered"
                    checked={formData.mark_delivered}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Mark shipment as delivered
                  </label>
                </div>
              </>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  ediType === '210' ? 'Create Invoice' : `Generate EDI ${ediType}`
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EDIModal; 